import { useEffect, useRef } from 'react';
import { useLoaderData } from 'react-router';
import { useTracker } from '@archipro-website/tracker';

import { useAddToCart } from '@modules/ecommerce/hook/use-add-to-cart';
import type { CartItem } from '@modules/ecommerce/type/cart-item';
import { useCartPopupState } from '~/modules/ecommerce/context/CartPopupContext';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { getProductGA4Data } from '~/modules/tracking/util/ga4Helper';
import { getEcomEventData } from '~/modules/tracking/util/gtmHelper';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';

interface UseAddToCartWithTrackingOptions {
    onItemAdded?: (item: CartItem | null) => void;
    /**
     * Open the cart popup / top-nav cart menu after a successful add
     * (default). Mobile surfaces pass false and confirm in their own sheet.
     */
    openCartPopup?: boolean;
}

/**
 * Add the selected variant to the cart with the full legacy side-effect set:
 * cart popup open, error toast, `AddToCart` tracker event and the GTM
 * `add_to_cart` remarketing event. Shared by the legacy purchase button group
 * and the revamp's inline mobile purchase flow so the behaviour stays
 * single-source. Must render inside `VariantSelectionProvider`.
 */
export const useAddToCartWithTracking = ({
    onItemAdded,
    openCartPopup = true,
}: UseAddToCartWithTrackingOptions = {}) => {
    const { quantity, selectedVariant } = useVariantSelectionState();
    const { product } = useLoaderData<ProductPageLoaderData>();
    const { onChange: onCartPopupChange } = useCartPopupState();
    const [addToCart, fetcher] = useAddToCart(false, {
        openCartMenu: openCartPopup,
    });
    const tracker = useTracker();
    const { toast } = useToaster();

    /** Adds the current selection to the cart and opens the cart popup. */
    const addSelectedToCart = () => {
        if (!selectedVariant) return;
        addToCart(selectedVariant, quantity);
        if (openCartPopup) onCartPopupChange(true);
    };

    // The fetcher keeps its `data` after settling (idle + data), so the effect
    // re-runs whenever a dependency identity changes (e.g. the inline
    // `onItemAdded` recreated each render by the desktop drawer). Track the last
    // processed result so each completed add fires its tracking + callback
    // exactly once instead of duplicating AddToCart / GTM events on re-render.
    const processedData = useRef<unknown>(null);

    useEffect(() => {
        if (fetcherIsDone(fetcher) && fetcher.data) {
            if (processedData.current === fetcher.data) return;
            processedData.current = fetcher.data;
            if ('error' in fetcher.data) {
                toast(fetcher.data.message || 'Unable to add to cart', {
                    type: 'error',
                });
            } else {
                const lastAddedItem =
                    fetcher.data?.addToShoppingCart.LastAddedItems[0];
                if (lastAddedItem) {
                    const ga4Data = getProductGA4Data({
                        product,
                        selectedVariant,
                        quantity,
                    });
                    tracker.log('AddToCart', {
                        url: new URL(window.location.href),
                        data: {
                            // format: CartItemID
                            ExtraData: `${lastAddedItem.ID || 0}`,
                        },
                    });
                    // ecom event for remarketing
                    tracker.log('add_to_cart', {
                        url: new URL(window.location.href),
                        targetTracker: 'googleTagManagerTracker',
                        gtmData: getEcomEventData(ga4Data),
                    });
                    onItemAdded?.({
                        ...lastAddedItem,
                    });
                }
            }
        }
    }, [
        fetcher,
        product,
        quantity,
        selectedVariant,
        toast,
        tracker,
        onItemAdded,
    ]);

    return { addSelectedToCart, isLoading: fetcher.state !== 'idle' };
};
